import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live snapshot of app version, DB latency, uptime, stuck orders and
 * open QR sessions. Auto-refreshes every 30s so admins have a passive
 * dashboard whenever they open Settings → Database.
 */
const SystemHealthCard = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetchHealth = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/system/health`);
      setHealth(r.data);
    } catch (e) {
      toast.error("Health check failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchHealth();
    const id = setInterval(fetchHealth, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dbLatencyTone = (ms) => ms == null ? "text-muted-foreground" : (ms < 50 ? "text-green-600" : ms < 200 ? "text-amber-600" : "text-red-600");

  return (
    <Card data-testid="system-health-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>System Health</CardTitle>
          <CardDescription>Live snapshot — refreshes every 30 seconds.</CardDescription>
        </div>
        <Button variant="outline" onClick={fetchHealth} disabled={loading} data-testid="health-refresh-btn">
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      </CardHeader>
      <CardContent>
        {!health ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <HealthTile label="App Version" value={health.version} data-testid="health-version" />
            <HealthTile label="Uptime" value={health.uptime_human || "—"} data-testid="health-uptime" />
            <HealthTile
              label="DB Latency"
              value={health.db_ok ? `${health.db_latency_ms ?? "—"} ms` : "DOWN"}
              tone={health.db_ok ? dbLatencyTone(health.db_latency_ms) : "text-red-600"}
              data-testid="health-db"
            />
            <HealthTile
              label="Stuck Orders (>30 min)"
              value={health.stuck_orders_count}
              tone={health.stuck_orders_count === 0 ? "text-green-600" : "text-amber-700"}
              data-testid="health-stuck-orders"
            />
            <HealthTile
              label="Abandoned QR Sessions"
              value={health.abandoned_sessions_count}
              tone={health.abandoned_sessions_count === 0 ? "text-green-600" : "text-amber-700"}
              data-testid="health-abandoned"
            />
            <HealthTile
              label="Last Card Auth"
              value={health.last_card_auth_at ? new Date(health.last_card_auth_at).toLocaleString() : "Never"}
              tone="text-muted-foreground"
              valueClass="text-sm"
              data-testid="health-last-card"
            />
            <HealthTile label="Active Tables" value={health.counts?.tables ?? 0} data-testid="health-tables" />
            <HealthTile label="Open Orders" value={health.counts?.open_orders ?? 0} data-testid="health-open-orders" />
            <HealthTile label="Active QR Sessions" value={health.counts?.self_order_sessions_active ?? 0} data-testid="health-sessions" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const HealthTile = ({ label, value, tone = "text-foreground", valueClass = "text-2xl font-mono font-semibold", ...rest }) => (
  <div className="rounded-lg border border-border p-4 bg-card" {...rest}>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={`mt-1 ${valueClass} ${tone}`}>{value}</div>
  </div>
);

export default SystemHealthCard;
